import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import background from '../../../assets/app/background.png'
import { readScrollPosition, saveScrollPosition, restoreScrollPosition } from '../../../utils/scrollRestore';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    // Flex items default to min-width:auto, which lets wide content (the hero
    // carousel's 7-slide strip) blow <main> out to ~11424px — upscaling/blurring the
    // slide backgrounds. min-width:0 keeps <main> at the container (viewport) width.
    minWidth: 0,
    overflowX: 'hidden',
    padding: 0,
    minHeight: '100vh',
    // width: '100vw',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

/**
 * @param restoreScroll  opt in to remembering this page's scroll position
 *   across a remount, keyed by `scrollKey` (defaults to the pathname). Off by
 *   default so every page that does not ask keeps the plain scroll-to-top.
 *   The home feed asks, because listings became a route and pressing Back now
 *   remounts the feed instead of dismissing a modal over it.
 */
export default function PageTemplate({ children, restoreScroll = false, scrollKey }) {
  const [open] = React.useState(false);

  const key = scrollKey || (typeof window !== 'undefined' ? window.location.pathname : '/');

  React.useEffect(() => {
    if (!restoreScroll) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const saved = readScrollPosition(key);
    const cancelRestore = saved ? restoreScrollPosition(saved) : (window.scrollTo(0, 0), null);

    // Recorded on scroll rather than on unmount: by the time React tears this
    // tree down the browser may already have moved, and the position saved
    // would be the next page's.
    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        saveScrollPosition(key, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      if (cancelRestore) cancelRestore();
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreScroll, key]);

  return (
    <Box sx={{ display: 'flex', backgroundImage: `url(${background})`, backgroundSize: 'cover', width: '100%', maxWidth: '100%', overflowX: 'hidden' }} >
      <CssBaseline />
      <Main open={open} >
        {children}
      </Main>
    </Box>
  );
}
